/**
 * Common types used across the application
 */
import { z } from 'zod';
export var UserRole;
(function (UserRole) {
    UserRole["USER"] = "USER";
    UserRole["PRESENTER"] = "PRESENTER";
    UserRole["ADMIN"] = "ADMIN";
})(UserRole || (UserRole = {}));
// ============================================================================
// Authentication Types
// ============================================================================
export var AuthProvider;
(function (AuthProvider) {
    AuthProvider["EMAIL"] = "email";
    AuthProvider["GOOGLE"] = "google";
    AuthProvider["MICROSOFT"] = "microsoft";
    AuthProvider["APPLE"] = "apple";
    AuthProvider["SEZNAM"] = "seznam";
    AuthProvider["MUNI_SAML"] = "muni_saml";
    AuthProvider["EDU_ID"] = "edu_id";
})(AuthProvider || (AuthProvider = {}));
// ============================================================================
// Screen Format
// ============================================================================
/**
 * Supported screen/video formats
 */
export var ScreenFormat;
(function (ScreenFormat) {
    ScreenFormat["JPG"] = "JPG";
    ScreenFormat["MJPG"] = "MJPG";
    ScreenFormat["MP4"] = "MP4";
    ScreenFormat["HLS"] = "HLS";
    ScreenFormat["RTSP"] = "RTSP";
    ScreenFormat["NDI"] = "NDI";
})(ScreenFormat || (ScreenFormat = {}));
/**
 * What a reader with no stored settings sees — a new user, a private window,
 * or cleared site data. Both transcript surfaces (`/view/transcript` and the
 * live-session panel in `/view/session`) and the app's prebundled viewer start
 * from this object. Stored settings still win: every viewer persists its whole
 * appearance on first render, so a reader who opened a viewer while the
 * default was 1.6 keeps 1.6 until they change it or press "Výchozí".
 */
export const DEFAULT_TRANSCRIPT_VIEWER_SETTINGS = Object.freeze({
    fontSize: 16,
    fontFamily: 'sans-serif',
    fontWeight: 'normal',
    // MU 17. 9. 2026: "výchozí hodnota prokladu řádků 1,2" (was 1.6).
    lineHeight: 1.2,
    textColor: '#1e293b',
    backgroundColor: '#ffffff',
    autoScroll: true,
});
// ============================================================================
// Zod Schemas
// ============================================================================
// Defaults come from DEFAULT_TRANSCRIPT_VIEWER_SETTINGS so the schema cannot
// drift from what the viewers render (it said 1.5 / #000000 while they said 1.2).
const TVS_DEFAULTS = DEFAULT_TRANSCRIPT_VIEWER_SETTINGS;
export const TranscriptViewerSettingsSchema = z.object({
    fontSize: z.number().min(12).max(96).default(TVS_DEFAULTS.fontSize),
    fontFamily: z
        .enum(['sans-serif', 'serif', 'monospace', 'opendyslexic', 'open-sans', 'roboto', 'gelasio'])
        .default(TVS_DEFAULTS.fontFamily),
    fontWeight: z.enum(['normal', 'bold']).default(TVS_DEFAULTS.fontWeight),
    lineHeight: z.number().min(1.0).max(3.0).default(TVS_DEFAULTS.lineHeight),
    textColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .default(TVS_DEFAULTS.textColor),
    backgroundColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .default(TVS_DEFAULTS.backgroundColor),
    autoScroll: z.boolean().default(TVS_DEFAULTS.autoScroll),
});
export const ScreenViewerSettingsSchema = z.object({
    format: z.nativeEnum(ScreenFormat).default(ScreenFormat.JPG),
    refreshInterval: z.number().min(0.5).max(10).default(1),
    grayscale: z.boolean().default(false),
    inverted: z.boolean().default(false),
    // Ceiling raised 3.0 → 5.0 (MU 7. 8. 2026) — 300 % was not enough for some
    // low-vision users. Must match MAX_ZOOM in the client screen viewer.
    zoomLevel: z.number().min(0.5).max(5.0).default(1.0),
    zoomLocked: z.boolean().default(false),
});
export const ScreenSourceSchema = z.object({
    id: z.string(),
    name: z.string(),
    format: z.nativeEnum(ScreenFormat),
    endpoint: z.string().url(),
    isActive: z.boolean(),
});
export const UserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().optional(),
    image: z.string().url().optional(),
    role: z.nativeEnum(UserRole),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
//# sourceMappingURL=types.js.map